/**
 * Field Visibility Definitions
 * 
 * Master field definitions for all views.
 * Each field has:
 * - id: unique identifier (used in settings object)
 * - label: display name in dropdown
 * - labelByView: view-specific label overrides
 * - category: grouping in dropdown
 * - views: which views this field applies to
 * - defaultEnabled: default visibility per view (or single boolean for all)
 * - mandatory: views where this field cannot be hidden
 */

export const FIELD_DEFINITIONS = [
  // ═══════════════════════════════════════════════════════════════
  // CORE FIELDS
  // ═══════════════════════════════════════════════════════════════
  // Note: Title/Name and Checkbox are mandatory in all views that have them,
  // so they're not listed here (no point showing options users can't toggle)
  {
    id: 'icon',
    label: 'Mission Icon',
    category: 'core',
    views: ['table', 'flow', 'kanban', 'todolist', 'neural'],
    defaultEnabled: true,
  },
  
  // ═══════════════════════════════════════════════════════════════
  // STATUS & META FIELDS
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'status',
    label: 'Status',
    category: 'meta',
    views: ['table', 'flow', 'kanban', 'todolist', 'neural'],
    defaultEnabled: true,
  },
  {
    id: 'priority',
    label: 'Priority',
    category: 'meta',
    views: ['table', 'flow', 'kanban', 'todolist', 'neural'],
    defaultEnabled: { table: true, flow: false, kanban: false, todolist: true, neural: false },
  },
  {
    id: 'difficulty',
    label: 'Difficulty',
    category: 'meta',
    views: ['table', 'flow', 'kanban', 'todolist', 'neural'],
    defaultEnabled: false,
  },
  {
    id: 'tags',
    label: 'Tags',
    category: 'meta',
    views: ['table', 'flow', 'kanban', 'todolist', 'neural'],
    defaultEnabled: true,
  },
  
  // ═══════════════════════════════════════════════════════════════
  // CONTENT / ARTIFACTS FIELDS
  // These show as columns in Table/TodoList, buttons in Flow/Kanban
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'attachments',
    label: 'Attachments',
    category: 'content',
    views: ['table', 'flow', 'kanban', 'todolist', 'neural'],
    defaultEnabled: { table: false, flow: true, kanban: true, todolist: false, neural: true },
  },
  {
    id: 'attachedDocs',
    label: 'Attached Docs',
    category: 'content',
    views: ['flow', 'kanban', 'neural'],
    defaultEnabled: true,
  },
  {
    id: 'detailsButton',
    label: 'Open Mission Button',
    category: 'content',
    views: ['table', 'flow', 'kanban', 'todolist', 'neural'],
    defaultEnabled: true,
  },
  {
    id: 'archiveButton',
    label: 'Archive Button',
    category: 'content',
    views: ['table', 'flow', 'kanban', 'todolist', 'neural'],
    defaultEnabled: { table: true, flow: false, kanban: false, todolist: true, neural: false },
  },
  {
    id: 'deleteButton',
    label: 'Delete Button',
    category: 'content',
    views: ['table', 'flow', 'kanban', 'todolist', 'neural'],
    defaultEnabled: true,
  },
  {
    id: 'openInViewButton',
    label: 'Open in View',
    category: 'content',
    views: ['table', 'flow', 'kanban', 'todolist', 'neural'],
    defaultEnabled: { table: true, flow: false, kanban: false, todolist: true, neural: false },
  },
  
  // ═══════════════════════════════════════════════════════════════
  // DATE FIELDS
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'dueDate',
    label: 'Due Date',
    category: 'dates',
    views: ['table', 'flow', 'kanban', 'todolist', 'neural'],
    defaultEnabled: { table: false, flow: true, kanban: true, todolist: true, neural: true },
  },
  {
    id: 'estimatedDurationMinutes',
    label: 'Estimated time',
    category: 'dates',
    views: ['table', 'flow', 'kanban', 'todolist', 'neural'],
    defaultEnabled: { table: false, flow: false, kanban: false, todolist: true, neural: false },
  },
  {
    id: 'createdAt',
    label: 'Created Date',
    category: 'dates',
    views: ['table', 'flow', 'kanban', 'todolist', 'neural'],
    defaultEnabled: false,
  },
  {
    id: 'startedAt',
    label: 'Start Date',
    category: 'dates',
    views: ['table', 'flow', 'kanban', 'todolist', 'neural'],
    defaultEnabled: false,
  },
  {
    id: 'completedAt',
    label: 'Completed Date',
    category: 'dates',
    views: ['table', 'flow', 'kanban', 'todolist', 'neural'],
    defaultEnabled: false,
  },
  {
    id: 'updatedAt',
    label: 'Last Updated',
    category: 'dates',
    views: ['table', 'flow', 'kanban', 'todolist', 'neural'],
    defaultEnabled: false,
  },
  
  // ═══════════════════════════════════════════════════════════════
  // CUSTOM FIELDS
  // Individual custom fields are passed via the customFields prop
  // and rendered in the 'custom' category section of the dropdown.
  // No static definitions here - they come from campaign data.
  // ═══════════════════════════════════════════════════════════════
];

// Category definitions
export const FIELD_CATEGORIES = {
  core: 'Core',
  meta: 'Metadata',
  content: 'Content',
  dates: 'Dates',
  custom: 'Custom',
};

// Category order for display
export const CATEGORY_ORDER = ['core', 'meta', 'content', 'dates', 'custom'];
